import {useState, useMemo, useEffect} from "react";
import {useQuery, useMutation} from "@tanstack/react-query";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Badge} from "@/components/ui/badge";
import {ScrollArea} from "@/components/ui/scroll-area";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@/components/ui/tabs";
import {Checkbox} from "@/components/ui/checkbox";
import {Separator} from "@/components/ui/separator";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {mastermindSpacesService} from "@/services/mastermindSpacesService";
import {topicsService} from "@/services/topicsService";
import {tagsService} from "@/services/tagsService";
import {useWorkspace} from "@/contexts/WorkspaceContext";
import {getTopicIcon} from "@/lib/utils";
import {queryClient} from "@/lib/queryClient";
import {useTags} from "@/hooks/useTags";
import {useToast} from "@/hooks/use-toast";
import {
    Search,
    Target,
    FolderOpen,
    Tag,
    Check,
    Layers,
    Plus,
    Loader2,
    MoreVertical,
    Edit,
    Trash2,
} from "lucide-react";
import type {MastermindSpace, Topic, Tag as TagType} from "@shared/schema";

interface ThreadLinkingDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    linkedSpaceIds: string[];
    topicId: string | null;
    tags: string[];
    onUpdateSpaces: (spaceIds: string[]) => void;
    onUpdateTopic?: (topicId: string | null) => void; // Optional when allowMultipleTopics is true
    onUpdateTags: (tags: string[]) => void;
    title?: string;
    description?: string;
    /** When true, Done button commits current selection to parent (callbacks) then closes. When false (default), Done only closes; parent already receives updates on each toggle. Use true for DocumentCreator so space/topic IDs are applied on Done. */
    commitSelectionOnDone?: boolean;
    /** When true, allows multiple topic selection (for documents). When false (default), only single topic selection (for threads). */
    allowMultipleTopics?: boolean;
    /** For documents with multiple topics: array of topic IDs. If provided, topicId is ignored. */
    topicIds?: string[];
    /** For documents with multiple topics: callback with array of topic IDs. If provided, onUpdateTopic is ignored. */
    onUpdateTopics?: (topicIds: string[]) => void;
    /**
     * When set, shared-space editors can only link to this mastermind space id (no other spaces).
     * Omit or leave null for no restriction (owners / personal context).
     */
    editorLinksRestrictedToSpaceId?: string | null;
    /** Linked space IDs that cannot be removed (e.g. topic home space). */
    requiredSpaceIds?: string[];
}

export function ThreadLinkingDialog({
                                        open,
                                        onOpenChange,
                                        linkedSpaceIds,
                                        topicId,
                                        tags,
                                        onUpdateSpaces,
                                        onUpdateTopic,
                                        onUpdateTags,
                                        title = "Link Thread",
                                        description = "Connect this chat thread to spaces, topics, and tags for better organization",
                                        commitSelectionOnDone = false,
                                        allowMultipleTopics = false,
                                        topicIds,
                                        onUpdateTopics,
                                        editorLinksRestrictedToSpaceId = null,
                                        requiredSpaceIds,
                                    }: ThreadLinkingDialogProps) {


    const {toast} = useToast();
    const [searchQuery, setSearchQuery] = useState("");
    const [activeTab, setActiveTab] = useState("spaces");
    const [newTag, setNewTag] = useState("");
    const [spacePickerOpen, setSpacePickerOpen] = useState(false);
    const [spacePickerSearch, setSpacePickerSearch] = useState("");
    const [isUpdating, setIsUpdating] = useState(false);
    const [editTagDialogOpen, setEditTagDialogOpen] = useState(false);
    const [tagToEdit, setTagToEdit] = useState<TagType | null>(null);
    const [editTagName, setEditTagName] = useState("");
    const [deleteTagDialogOpen, setDeleteTagDialogOpen] = useState(false);
    const [tagToDelete, setTagToDelete] = useState<TagType | null>(null);

    const [localLinkedSpaceIds, setLocalLinkedSpaceIds] = useState<string[]>(linkedSpaceIds);
    const [localTopicId, setLocalTopicId] = useState<string | null>(topicId);
    const [localTopicIds, setLocalTopicIds] = useState<string[]>(topicIds || []);
    const [localTags, setLocalTags] = useState<string[]>(tags);

    const requiredSpaceIdSet = useMemo(
        () => new Set((requiredSpaceIds ?? []).filter(Boolean)),
        [requiredSpaceIds]
    );

    useEffect(() => {
        const merged = [...new Set([...(requiredSpaceIds ?? []).filter(Boolean), ...linkedSpaceIds])];
        setLocalLinkedSpaceIds((prev) => {
            const a = JSON.stringify([...prev].sort());
            const b = JSON.stringify([...merged].sort());
            return a === b ? prev : merged;
        });
    }, [linkedSpaceIds, requiredSpaceIds]);

    useEffect(() => {
        setLocalTopicId(topicId);
    }, [topicId]);

    useEffect(() => {
        if (allowMultipleTopics) {
            // Always use topicIds array if provided, otherwise use empty array
            const newTopicIds = Array.isArray(topicIds) ? topicIds : [];
            // Use deep comparison to avoid unnecessary updates
            const currentIds = JSON.stringify([...localTopicIds].sort());
            const newIds = JSON.stringify([...newTopicIds].sort());
            if (currentIds !== newIds) {
                setLocalTopicIds(newTopicIds);
            }
        }
    }, [topicIds, allowMultipleTopics]);

    useEffect(() => {
        setLocalTags(tags);
    }, [tags]);

    const {currentWorkspaceId} = useWorkspace();
    const {data: spaces = []} = useQuery<MastermindSpace[]>({
        queryKey: ["/api/spaces", currentWorkspaceId ?? null],
        queryFn: async () => {
            return await mastermindSpacesService.getMastermindSpaces(currentWorkspaceId ?? null);
        },
    });

    const {data: allTopics = []} = useQuery<Topic[]>({
        queryKey: ["/api/all-topics"],
        queryFn: async () => {
            return await topicsService.getAllTopics();
        },
    });


    const {data: allTags = []} = useTags();

    const restrictedSpaceId = editorLinksRestrictedToSpaceId?.trim() || null;

    const isSpaceBlockedForEditor = (space: MastermindSpace) =>
        !!restrictedSpaceId && space.id !== restrictedSpaceId;

    const createTopicMutation = useMutation({
        mutationFn: async ({name, spaceId}: { name: string; spaceId: string }) => {
            const sp = spaces.find((s) => s.id === spaceId);
            if (sp && isSpaceBlockedForEditor(sp)) {
                throw new Error(
                    "Editors can only link items within the current shared space."
                );
            }
            return await topicsService.createTopic({name, spaceId});
        },
        onSuccess: async (newTopic) => {
            setIsUpdating(true);
            try {
                queryClient.invalidateQueries({queryKey: ["/api/all-topics"]});
                queryClient.invalidateQueries({queryKey: ["/api/topics"]});
                setLocalTopicId(newTopic.id);
                await Promise.resolve(onUpdateTopic?.(newTopic.id));
                if (!localLinkedSpaceIds.includes(newTopic.spaceId)) {
                    const newIds = [...localLinkedSpaceIds, newTopic.spaceId];
                    setLocalLinkedSpaceIds(newIds);
                    await Promise.resolve(onUpdateSpaces(newIds));
                }
                setSearchQuery("");
                toast({
                    title: "Topic created",
                    description: `"${newTopic.name}" has been created and linked.`,
                });
            } finally {
                setTimeout(() => setIsUpdating(false), 300);
            }
        },
        onError: (error: Error) => {
            toast({
                title: "Failed to create topic",
                description: error.message,
                variant: "destructive",
            });
        },
    });


    const filteredSpaces = useMemo(() => {
        let result = spaces;
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            result = spaces.filter(
                (space) =>
                    space.name.toLowerCase().includes(query) ||
                    space.description?.toLowerCase().includes(query)
            );
        }
        return [...result].sort((a, b) => {
            const aLinked = localLinkedSpaceIds.includes(a.id);
            const bLinked = localLinkedSpaceIds.includes(b.id);
            if (aLinked && !bLinked) return -1;
            if (!aLinked && bLinked) return 1;
            return a.name.localeCompare(b.name);
        });
    }, [spaces, searchQuery, localLinkedSpaceIds]);

    const filteredTopics = useMemo(() => {
        // First, filter to only topics from linked spaces
        let result = allTopics.filter((topic) =>
            localLinkedSpaceIds.includes(topic.spaceId)
        );

        // Then filter by search query if present
        if (searchQuery.trim()) {
            const queryWords = searchQuery.toLowerCase().split(/\s+/);
            result = result.filter((topic) => {
                const topicName = topic.name.toLowerCase();
                return queryWords.every(word => topicName.includes(word));
            });
        }

        return [...result].sort((a, b) => {
            if (allowMultipleTopics) {
                const aSelected = localTopicIds.includes(a.id);
                const bSelected = localTopicIds.includes(b.id);
                if (aSelected && !bSelected) return -1;
                if (!aSelected && bSelected) return 1;
            } else {
                const aSelected = String(localTopicId) === String(a.id);
                const bSelected = String(localTopicId) === String(b.id);
                if (aSelected && !bSelected) return -1;
                if (!aSelected && bSelected) return 1;
            }
            return a.name.localeCompare(b.name);
        });
    }, [allTopics, searchQuery, localTopicId, localTopicIds, localLinkedSpaceIds, allowMultipleTopics]);

    const filteredTags = useMemo(() => {
        let result = allTags;
        if (searchQuery.trim()) {
            const queryWords = searchQuery.toLowerCase().split(/\s+/);
            result = allTags.filter((tag) => {
                const tagName = tag.name.toLowerCase();
                return queryWords.every(word => tagName.includes(word));
            });
        }
        return [...result].sort((a, b) => {
            const aSelected = localTags.includes(a.name);
            const bSelected = localTags.includes(b.name);
            if (aSelected && !bSelected) return -1;
            if (!aSelected && bSelected) return 1;
            return a.name.localeCompare(b.name);
        });
    }, [allTags, searchQuery, localTags]);

    const handleToggleSpace = async (spaceId: string) => {
        if (isUpdating) return; // Prevent multiple clicks during update

        setIsUpdating(true);
        try {
            if (localLinkedSpaceIds.includes(spaceId)) {
                if (requiredSpaceIdSet.has(spaceId)) {
                    toast({
                        title: "Home space required",
                        description:
                            "This topic's home space cannot be removed here. Use Move topic to change its home space.",
                        variant: "destructive",
                    });
                    return;
                }
                // Space is being removed
                const newIds = localLinkedSpaceIds.filter((id) => id !== spaceId);
                setLocalLinkedSpaceIds(newIds);
                await Promise.resolve(onUpdateSpaces(newIds));

                // Only remove topics that belong to the removed space
                // Keep topics from other spaces intact
                const removedSpaceTopics = allTopics
                    .filter((t) => t.spaceId === spaceId)
                    .map((t) => t.id);

                if (allowMultipleTopics && onUpdateTopics) {
                    // Filter out only topics from the removed space
                    const newTopicIds = localTopicIds.filter((tid) => !removedSpaceTopics.includes(tid));
                    setLocalTopicIds(newTopicIds);
                    await Promise.resolve(onUpdateTopics(newTopicIds));
                } else if (onUpdateTopic && localTopicId && removedSpaceTopics.includes(localTopicId)) {
                    // For single topic mode, clear if it belongs to removed space
                    setLocalTopicId(null);
                    await Promise.resolve(onUpdateTopic(null));
                }
            } else {
                const sp = spaces.find((s) => s.id === spaceId);
                if (sp && isSpaceBlockedForEditor(sp)) {
                    toast({
                        title: "Cannot link to other spaces",
                        description:
                            "As an editor, you can only link items within this shared space.",
                        variant: "destructive",
                    });
                    return;
                }
                // Space is being added
                const newIds = [...localLinkedSpaceIds, spaceId];
                setLocalLinkedSpaceIds(newIds);
                await Promise.resolve(onUpdateSpaces(newIds));

                // When a new space is added, keep all existing topics
                // Topics are already filtered by space in the UI, so no need to filter here
                // The user can manually select topics from the new space if they want
            }
        } finally {
            // Add a small delay to ensure UI updates are visible
            setTimeout(() => setIsUpdating(false), 300);
        }
    };

    const handleSelectTopic = async (selectedTopicId: string) => {
        if (isUpdating) return; // Prevent multiple clicks during update

        setIsUpdating(true);
        try {
            if (allowMultipleTopics && onUpdateTopics) {
                // Multiple topic selection for documents
                if (localTopicIds.includes(selectedTopicId)) {
                    const newTopicIds = localTopicIds.filter((id) => id !== selectedTopicId);
                    setLocalTopicIds(newTopicIds);
                    await Promise.resolve(onUpdateTopics(newTopicIds));
                } else {
                    const newTopicIds = [...localTopicIds, selectedTopicId];
                    setLocalTopicIds(newTopicIds);
                    await Promise.resolve(onUpdateTopics(newTopicIds));
                }
            } else if (onUpdateTopic) {
                // Single topic selection for threads
                if (localTopicId === selectedTopicId) {
                    setLocalTopicId(null);
                    await Promise.resolve(onUpdateTopic(null));
                } else {
                    setLocalTopicId(selectedTopicId);
                    await Promise.resolve(onUpdateTopic(selectedTopicId));
                }
            }
        } finally {
            // Add a small delay to ensure UI updates are visible
            setTimeout(() => setIsUpdating(false), 300);
        }
    };

    const handleAddTag = async () => {
        if (isUpdating || !newTag.trim() || localTags.includes(newTag.trim())) return;

        setIsUpdating(true);
        try {
            const tagName = newTag.trim();

            // Check if tag already exists in database
            let existingTag = allTags.find(t => t.name.toLowerCase() === tagName.toLowerCase());

            if (!existingTag) {
                // Create tag in database
                try {
                    existingTag = await tagsService.createTag({name: tagName});
                    // Invalidate tags query to refresh the list
                    queryClient.invalidateQueries({queryKey: ["/api/tags"]});
                    toast({
                        title: "Tag created",
                        description: `Tag "${tagName}" has been created and added.`,
                    });
                } catch (error: any) {
                    // If tag creation fails (e.g., duplicate), try to find it again
                    if (error.message?.includes("already exists")) {
                        // Refetch tags to get the existing one
                        await queryClient.refetchQueries({queryKey: ["/api/tags"]});
                        // Try to get the tag by name from the service
                        try {
                            const foundTag = await tagsService.getTagByName(tagName);
                            existingTag = foundTag || undefined;
                        } catch {
                            // If that fails, just use the tag name as-is
                            existingTag = undefined;
                        }
                    } else {
                        throw error;
                    }
                }
            }

            // Add tag name to local tags (use the actual tag name from DB for consistency)
            const tagNameToAdd = existingTag?.name || tagName;
            if (!localTags.includes(tagNameToAdd)) {
                const newTags = [...localTags, tagNameToAdd];
                setLocalTags(newTags);
                await Promise.resolve(onUpdateTags(newTags));
            }

            setNewTag("");
        } catch (error: any) {
            toast({
                title: "Failed to create tag",
                description: error.message || "Could not create tag",
                variant: "destructive",
            });
        } finally {
            setTimeout(() => setIsUpdating(false), 300);
        }
    };

    const handleToggleTag = async (tagName: string) => {
        if (isUpdating) return; // Prevent multiple clicks during update

        setIsUpdating(true);
        try {
            if (localTags.includes(tagName)) {
                const newTags = localTags.filter((t) => t !== tagName);
                setLocalTags(newTags);
                await Promise.resolve(onUpdateTags(newTags));
            } else {
                const newTags = [...localTags, tagName];
                setLocalTags(newTags);
                await Promise.resolve(onUpdateTags(newTags));
            }
        } finally {
            setTimeout(() => setIsUpdating(false), 300);
        }
    };

    const getSpaceForTopic = (topic: Topic) => {
        return spaces.find((s) => s.id === topic.spaceId);
    };

    const handleEditTag = (tag: TagType) => {
        setTagToEdit(tag);
        setEditTagName(tag.name);
        setEditTagDialogOpen(true);
    };

    const handleConfirmEditTag = async () => {
        if (!tagToEdit || !editTagName.trim() || editTagName.trim() === tagToEdit.name) {
            setEditTagDialogOpen(false);
            return;
        }

        setIsUpdating(true);
        try {
            const updatedTag = await tagsService.updateTag(tagToEdit.id, {name: editTagName.trim()});

            if (updatedTag) {
                // Invalidate tags query to refresh the list
                queryClient.invalidateQueries({queryKey: ["/api/tags"]});

                // Update local tags if the edited tag was selected
                const oldName = tagToEdit.name;
                const newName = updatedTag.name;

                if (localTags.includes(oldName)) {
                    const newTags = localTags.map(t => t === oldName ? newName : t);
                    setLocalTags(newTags);
                    await Promise.resolve(onUpdateTags(newTags));
                }

                toast({
                    title: "Tag updated",
                    description: `Tag has been renamed to "${newName}".`,
                });
            }

            setEditTagDialogOpen(false);
            setTagToEdit(null);
            setEditTagName("");
        } catch (error: any) {
            toast({
                title: "Failed to update tag",
                description: error.message || "Could not update tag",
                variant: "destructive",
            });
        } finally {
            setTimeout(() => setIsUpdating(false), 300);
        }
    };

    const handleDeleteTag = (tag: TagType) => {
        setTagToDelete(tag);
        setDeleteTagDialogOpen(true);
    };

    const handleConfirmDeleteTag = async () => {
        if (!tagToDelete) {
            setDeleteTagDialogOpen(false);
            return;
        }

        setIsUpdating(true);
        try {
            const deleted = await tagsService.deleteTag(tagToDelete.id);

            if (deleted) {
                // Invalidate tags query to refresh the list
                queryClient.invalidateQueries({queryKey: ["/api/tags"]});

                // Remove tag from local tags if it was selected
                if (localTags.includes(tagToDelete.name)) {
                    const newTags = localTags.filter(t => t !== tagToDelete.name);
                    setLocalTags(newTags);
                    await Promise.resolve(onUpdateTags(newTags));
                }

                toast({
                    title: "Tag deleted",
                    description: `Tag "${tagToDelete.name}" has been deleted.`,
                });
            }

            setDeleteTagDialogOpen(false);
            setTagToDelete(null);
        } catch (error: any) {
            toast({
                title: "Failed to delete tag",
                description: error.message || "Could not delete tag",
                variant: "destructive",
            });
        } finally {
            setTimeout(() => setIsUpdating(false), 300);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Layers className="h-5 w-5"/>
                        {title}
                    </DialogTitle>
                    <DialogDescription>
                        {description}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex items-center gap-2 px-1">
                    <div className="relative flex-1">
                        <Search
                            className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground"/>
                        <Input
                            placeholder="Search spaces, topics, and tags..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9"
                        />
                    </div>
                </div>

                <Tabs
                    value={activeTab}
                    onValueChange={setActiveTab}
                    className="flex-1 flex flex-col min-h-0"
                >
                    <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="spaces" className="gap-2">
                            <Target className="h-4 w-4"/>
                            Spaces
                            {localLinkedSpaceIds.length > 0 && (
                                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                                    {localLinkedSpaceIds.length}
                                </Badge>
                            )}
                        </TabsTrigger>
                        <TabsTrigger value="topics" className="gap-2">
                            <FolderOpen className="h-4 w-4"/>
                            Topics
                            {(allowMultipleTopics ? localTopicIds.length > 0 : localTopicId) && (
                                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                                    {allowMultipleTopics ? localTopicIds.length : 1}
                                </Badge>
                            )}
                        </TabsTrigger>
                        <TabsTrigger value="tags" className="gap-2">
                            <Tag className="h-4 w-4"/>
                            Tags
                            {localTags.length > 0 && (
                                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                                    {localTags.length}
                                </Badge>
                            )}
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="spaces" className="flex-1 mt-4 min-h-0">
                        <ScrollArea className="h-[300px] pr-4">
                            <div className="space-y-2">
                                {filteredSpaces.length === 0 ? (
                                    <div className="text-center py-8 text-muted-foreground">
                                        {searchQuery ? "No spaces match your search" : "No spaces available"}
                                    </div>
                                ) : (
                                    filteredSpaces.map((space) => {
                                        const isLinked = localLinkedSpaceIds.includes(space.id);
                                        const blockedAdd = !isLinked && isSpaceBlockedForEditor(space);
                                        const lockedLinked = isLinked && requiredSpaceIdSet.has(space.id);
                                        const spaceTopicsCount = allTopics.filter(
                                            (t) => t.spaceId === space.id
                                        ).length;
                                        return (
                                            <div
                                                key={space.id}
                                                className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                                                    isLinked
                                                        ? "bg-primary/5 border-primary/30"
                                                        : blockedAdd
                                                          ? "opacity-50 bg-muted/20"
                                                          : "hover:bg-muted/50"
                                                } ${isUpdating || blockedAdd || lockedLinked ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                                                title={
                                                    blockedAdd
                                                        ? "Editors can only link within this shared space"
                                                        : lockedLinked
                                                          ? "Home space — use Move topic to change"
                                                          : undefined
                                                }
                                                onClick={() =>
                                                    !isUpdating && !blockedAdd && !lockedLinked && handleToggleSpace(space.id)
                                                }
                                            >
                                                <Checkbox checked={isLinked} disabled={isUpdating || blockedAdd || lockedLinked}/>
                                                <div
                                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                                    style={{backgroundColor: space.color || "#888"}}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-medium text-sm truncate">
                                                        {space.name}
                                                    </div>
                                                    {space.description && (
                                                        <div className="text-xs text-muted-foreground truncate">
                                                            {space.description}
                                                        </div>
                                                    )}
                                                </div>
                                                {spaceTopicsCount > 0 && (
                                                    <Badge variant="outline" className="text-xs">
                                                        {spaceTopicsCount} topics
                                                    </Badge>
                                                )}
                                                {isLinked && <Check className="h-4 w-4 text-primary"/>}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </ScrollArea>
                    </TabsContent>

                    <TabsContent value="topics" className="flex-1 mt-4 min-h-0">
                        {searchQuery.trim() && (
                            <div className="mb-3 space-y-2">
                                {spaces.length === 1 ? (
                                    <Button
                                        variant="outline"
                                        className="w-full justify-start gap-2 border-dashed"
                                        onClick={() => {
                                            if (!isUpdating && !isSpaceBlockedForEditor(spaces[0])) {
                                                createTopicMutation.mutate({
                                                    name: searchQuery.trim(),
                                                    spaceId: spaces[0].id
                                                });
                                            }
                                        }}
                                        disabled={createTopicMutation.isPending || isUpdating || isSpaceBlockedForEditor(spaces[0])}
                                        data-testid="button-create-topic-quick"
                                    >
                                        <Plus className="h-4 w-4 text-primary flex-shrink-0"/>
                                        <span className="text-sm">
                      Create "<span className="font-medium">{searchQuery.trim()}</span>" in {spaces[0].name}
                    </span>
                                    </Button>
                                ) : (
                                    <Popover open={spacePickerOpen} onOpenChange={(open) => {
                                        setSpacePickerOpen(open);
                                        if (!open) setSpacePickerSearch("");
                                    }}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className="w-full justify-start gap-2 border-dashed"
                                                disabled={createTopicMutation.isPending || isUpdating}
                                                data-testid="button-create-topic-open"
                                            >
                                                {createTopicMutation.isPending ? (
                                                    <>
                                                        <Loader2
                                                            className="h-4 w-4 animate-spin text-primary flex-shrink-0"/>
                                                        <span className="text-sm">Creating topic...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Plus className="h-4 w-4 text-primary flex-shrink-0"/>
                                                        <span className="text-sm">
                              Create "<span className="font-medium">{searchQuery.trim()}</span>"
                            </span>
                                                    </>
                                                )}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-72 p-2" align="start">
                                            {(() => {
                                                const linkedSpaces = spaces.filter(s => localLinkedSpaceIds.includes(s.id));
                                                const otherSpaces = spaces.filter(s => !localLinkedSpaceIds.includes(s.id));
                                                const filteredOtherSpaces = spacePickerSearch.trim()
                                                    ? otherSpaces.filter(s => s.name.toLowerCase().includes(spacePickerSearch.toLowerCase()))
                                                    : otherSpaces;

                                                return (
                                                    <>
                                                        {linkedSpaces.length > 0 && (
                                                            <>
                                                                <div
                                                                    className="text-xs font-medium text-muted-foreground mb-1 px-2">
                                                                    Selected space{linkedSpaces.length > 1 ? 's' : ''}:
                                                                </div>
                                                                <div className="space-y-1 mb-2">
                                                                    {linkedSpaces.map((space) => (
                                                                        <button
                                                                            key={space.id}
                                                                            className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover-elevate active-elevate-2 text-left bg-primary/5 disabled:opacity-50 disabled:pointer-events-none"
                                                                            onClick={() => {
                                                                                if (!isUpdating && !isSpaceBlockedForEditor(space)) {
                                                                                    setSpacePickerOpen(false);
                                                                                    setSpacePickerSearch("");
                                                                                    createTopicMutation.mutate({
                                                                                        name: searchQuery.trim(),
                                                                                        spaceId: space.id
                                                                                    });
                                                                                }
                                                                            }}
                                                                            disabled={isUpdating || isSpaceBlockedForEditor(space)}
                                                                            data-testid={`button-create-topic-in-space-${space.id}`}
                                                                        >
                                                                            <div
                                                                                className="w-2 h-2 rounded-full flex-shrink-0"
                                                                                style={{backgroundColor: space.color || "#888"}}
                                                                            />
                                                                            <span
                                                                                className="truncate font-medium">{space.name}</span>
                                                                            <Check
                                                                                className="h-3 w-3 text-primary ml-auto flex-shrink-0"/>
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </>
                                                        )}

                                                        {otherSpaces.length > 0 && (
                                                            <>
                                                                <div className="relative mb-2">
                                                                    <Search
                                                                        className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground"/>
                                                                    <Input
                                                                        placeholder="Search other spaces..."
                                                                        value={spacePickerSearch}
                                                                        onChange={(e) => setSpacePickerSearch(e.target.value)}
                                                                        className="h-8 pl-7 text-sm"
                                                                        data-testid="input-space-search"
                                                                    />
                                                                </div>
                                                                <div className="max-h-32 overflow-y-auto space-y-1">
                                                                    {filteredOtherSpaces.length === 0 ? (
                                                                        <div
                                                                            className="text-xs text-muted-foreground text-center py-2">
                                                                            No spaces match
                                                                        </div>
                                                                    ) : (
                                                                        filteredOtherSpaces.map((space) => (
                                                                            <button
                                                                                key={space.id}
                                                                                className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover-elevate active-elevate-2 text-left disabled:opacity-50 disabled:pointer-events-none"
                                                                                onClick={() => {
                                                                                    if (!isUpdating && !isSpaceBlockedForEditor(space)) {
                                                                                        setSpacePickerOpen(false);
                                                                                        setSpacePickerSearch("");
                                                                                        createTopicMutation.mutate({
                                                                                            name: searchQuery.trim(),
                                                                                            spaceId: space.id
                                                                                        });
                                                                                    }
                                                                                }}
                                                                                disabled={isUpdating || isSpaceBlockedForEditor(space)}
                                                                                data-testid={`button-create-topic-in-space-${space.id}`}
                                                                            >
                                                                                <div
                                                                                    className="w-2 h-2 rounded-full flex-shrink-0"
                                                                                    style={{backgroundColor: space.color || "#888"}}
                                                                                />
                                                                                <span
                                                                                    className="truncate">{space.name}</span>
                                                                            </button>
                                                                        ))
                                                                    )}
                                                                </div>
                                                            </>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </PopoverContent>
                                    </Popover>
                                )}
                            </div>
                        )}
                        <ScrollArea className="h-[260px] pr-4">
                            {filteredTopics.length === 0 && searchQuery.trim() ? (
                                <div className="text-center py-6 text-muted-foreground">
                                    <FolderOpen className="h-10 w-10 mx-auto mb-2 opacity-50"/>
                                    <p className="font-medium">No existing topics match</p>
                                    <p className="text-sm">Use the form above to create one</p>
                                </div>
                            ) : filteredTopics.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <FolderOpen className="h-12 w-12 mx-auto mb-3 opacity-50"/>
                                    <p className="font-medium">No topics available</p>
                                    <p className="text-sm">Create topics in your spaces first, or search for a name to
                                        create one</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {filteredTopics.map((topic) => {
                                        const {icon: TopicIcon, color} = getTopicIcon(undefined);
                                        const isSelected = allowMultipleTopics
                                            ? localTopicIds.includes(topic.id)
                                            : String(localTopicId) === String(topic.id);
                                        const parentSpace = getSpaceForTopic(topic);
                                        return (
                                            <div
                                                key={topic.id}
                                                className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                                                    isSelected
                                                        ? "bg-primary/5 border-primary/30"
                                                        : "hover:bg-muted/50"
                                                } ${isUpdating ? "opacity-50 cursor-not-allowed pointer-events-none" : "cursor-pointer"}`}
                                                onClick={() => !isUpdating && handleSelectTopic(topic.id)}
                                            >
                                                <Checkbox
                                                    checked={isSelected}
                                                    disabled={isUpdating}
                                                    onCheckedChange={() => !isUpdating && handleSelectTopic(topic.id)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                                <div
                                                    className={`p-1.5 rounded-md ${
                                                        isSelected ? "bg-primary/10" : "bg-muted"
                                                    }`}
                                                >
                                                    <TopicIcon className={`h-4 w-4 ${color}`}/>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-medium text-sm">{topic.name}</div>
                                                    {parentSpace && (
                                                        <div
                                                            className="flex items-center gap-1 text-xs text-muted-foreground">
                                                            <div
                                                                className="w-2 h-2 rounded-full"
                                                                style={{
                                                                    backgroundColor: parentSpace.color || "#888",
                                                                }}
                                                            />
                                                            {parentSpace.name}
                                                        </div>
                                                    )}
                                                </div>
                                                {isSelected && <Check className="h-4 w-4 text-primary"/>}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </ScrollArea>
                    </TabsContent>

                    <TabsContent value="tags" className="flex-1 mt-4 min-h-0">
                        <div className="space-y-4">
                            <div className="flex gap-2">
                                <Input
                                    placeholder="Create a new tag..."
                                    value={newTag}
                                    onChange={(e) => setNewTag(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && !isUpdating) {
                                            e.preventDefault();
                                            handleAddTag();
                                        }
                                    }}
                                    className="flex-1"
                                    disabled={isUpdating}
                                />
                                <Button onClick={handleAddTag} disabled={!newTag.trim() || isUpdating}>
                                    {isUpdating ? <Loader2 className="h-4 w-4 animate-spin"/> : "Create"}
                                </Button>
                            </div>

                            <Separator/>

                            <ScrollArea className="h-[240px] pr-4">
                                {filteredTags.length === 0 && localTags.length === 0 ? (
                                    <div className="text-center py-8 text-muted-foreground">
                                        <Tag className="h-12 w-12 mx-auto mb-3 opacity-50"/>
                                        <p className="font-medium">
                                            {searchQuery ? "No tags match your search" : "No tags available"}
                                        </p>
                                        <p className="text-sm">
                                            {searchQuery ? "Try different search terms" : "Create your first tag above"}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {filteredTags.map((tag) => {
                                            const isSelected = localTags.includes(tag.name);
                                            return (
                                                <div
                                                    key={tag.id}
                                                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                                                        isSelected
                                                            ? "bg-primary/5 border-primary/30"
                                                            : "hover:bg-muted/50"
                                                    } ${isUpdating ? "opacity-50 cursor-not-allowed" : ""}`}
                                                >
                                                    <div
                                                        className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                                                        onClick={() => !isUpdating && handleToggleTag(tag.name)}
                                                    >
                                                        <Checkbox checked={isSelected} disabled={isUpdating}/>
                                                        <Tag className="h-4 w-4 text-muted-foreground flex-shrink-0"/>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="font-medium text-sm">{tag.name}</div>
                                                        </div>
                                                        {isSelected &&
                                                            <Check className="h-4 w-4 text-primary flex-shrink-0"/>}
                                                    </div>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild
                                                                             onClick={(e) => e.stopPropagation()}>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-7 w-7 flex-shrink-0"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <MoreVertical className="h-4 w-4"/>
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end"
                                                                             onClick={(e) => e.stopPropagation()}>
                                                            <DropdownMenuItem
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleEditTag(tag);
                                                                }}
                                                                disabled={isUpdating}
                                                            >
                                                                <Edit className="h-4 w-4 mr-2"/>
                                                                Edit
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteTag(tag);
                                                                }}
                                                                disabled={isUpdating}
                                                                className="text-destructive focus:text-destructive"
                                                            >
                                                                <Trash2 className="h-4 w-4 mr-2"/>
                                                                Delete
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </div>
                                            );
                                        })}
                                        {localTags
                                            .filter(tagName => !allTags.find(t => t.name === tagName))
                                            .map(tagName => (
                                                <div
                                                    key={`new-${tagName}`}
                                                    className="flex items-center gap-3 p-3 rounded-lg border bg-primary/5 border-primary/30 cursor-pointer transition-colors"
                                                    onClick={() => handleToggleTag(tagName)}
                                                >
                                                    <Checkbox checked={true}/>
                                                    <Tag className="h-4 w-4 text-muted-foreground"/>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-medium text-sm">{tagName}</div>
                                                        <div className="text-xs text-muted-foreground">New tag</div>
                                                    </div>
                                                    <Check className="h-4 w-4 text-primary"/>
                                                </div>
                                            ))}
                                    </div>
                                )}
                            </ScrollArea>
                        </div>
                    </TabsContent>
                </Tabs>

                <Separator/>

                <div className="flex items-center justify-between pt-2">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        {localLinkedSpaceIds.length > 0 && (
                            <span className="flex items-center gap-1">
                <Target className="h-3 w-3"/>
                                {localLinkedSpaceIds.length} space
                                {localLinkedSpaceIds.length !== 1 ? "s" : ""}
              </span>
                        )}
                        {(allowMultipleTopics ? localTopicIds.length > 0 : localTopicId) && (
                            <span className="flex items-center gap-1">
                <FolderOpen className="h-3 w-3"/>
                                {allowMultipleTopics ? localTopicIds.length : 1} topic
                                {(allowMultipleTopics ? localTopicIds.length : 1) !== 1 ? "s" : ""}
              </span>
                        )}
                        {localTags.length > 0 && (
                            <span className="flex items-center gap-1">
                <Tag className="h-3 w-3"/>
                                {localTags.length} tag{localTags.length !== 1 ? "s" : ""}
              </span>
                        )}
                    </div>
                    <Button
                        onClick={() => {
                            if (!isUpdating) {
                                if (commitSelectionOnDone) {
                                    onUpdateSpaces(localLinkedSpaceIds);
                                    if (allowMultipleTopics && onUpdateTopics) {
                                        onUpdateTopics(localTopicIds);
                                    } else {
                                        onUpdateTopic?.(localTopicId);
                                    }
                                    onUpdateTags(localTags);
                                }
                                onOpenChange(false);
                            }
                        }}
                        disabled={isUpdating}
                    >
                        {isUpdating ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2"/>
                                Updating...
                            </>
                        ) : (
                            "Done"
                        )}
                    </Button>
                </div>
            </DialogContent>

            {/* Edit Tag Dialog */}
            <Dialog open={editTagDialogOpen} onOpenChange={setEditTagDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Edit Tag</DialogTitle>
                        <DialogDescription>
                            Update the tag name. This will update the tag across all documents and threads.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <label htmlFor="edit-tag-name" className="text-sm font-medium">
                                Tag Name
                            </label>
                            <Input
                                id="edit-tag-name"
                                value={editTagName}
                                onChange={(e) => setEditTagName(e.target.value)}
                                placeholder="Enter tag name..."
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && editTagName.trim() && !isUpdating) {
                                        e.preventDefault();
                                        handleConfirmEditTag();
                                    }
                                }}
                                disabled={isUpdating}
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setEditTagDialogOpen(false);
                                setTagToEdit(null);
                                setEditTagName("");
                            }}
                            disabled={isUpdating}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmEditTag}
                            disabled={isUpdating || !editTagName.trim() || editTagName.trim() === tagToEdit?.name}
                        >
                            {isUpdating ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin mr-2"/>
                                    Updating...
                                </>
                            ) : (
                                "Update"
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete Tag Confirmation Dialog */}
            <AlertDialog open={deleteTagDialogOpen} onOpenChange={setDeleteTagDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Tag?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete the tag "{tagToDelete?.name}"? This will remove the tag from
                            all documents and threads that use it. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isUpdating}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleConfirmDeleteTag}
                            disabled={isUpdating}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {isUpdating ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin mr-2"/>
                                    Deleting...
                                </>
                            ) : (
                                "Delete"
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Dialog>
    );
}
